"use client";

import React, { useEffect, useState } from 'react';
import { ExternalLink, Globe, Play } from "lucide-react";
import { ERROR_MESSAGES } from '@/lib/dify/errorUtils';

interface LinkMetadata {
    title?: string;
    description?: string;
    image?: string;
    favicon?: string;
    siteName?: string;
    url: string;
}

type LinkPreviewVariant = "full" | "compact";

interface LinkPreviewProps {
    url: string;
    title?: string; // Optional fallback title
    variant?: LinkPreviewVariant;
}

// Video platform detection
const getVideoType = (url: string): 'youtube' | 'tiktok' | 'instagram' | null => {
    const urlLower = url.toLowerCase();
    if (urlLower.includes('youtube.com') || urlLower.includes('youtu.be')) {
        return 'youtube';
    }
    if (urlLower.includes('tiktok.com')) {
        return 'tiktok';
    }
    if (urlLower.includes('instagram.com')) {
        return 'instagram';
    }
    return null;
};

// Generate embed URL for videos
const getEmbedUrl = (url: string, type: 'youtube' | 'tiktok' | 'instagram'): string | null => {
    try {
        switch (type) {
            case 'youtube': {
                const urlObj = new URL(url);
                let videoId: string | null = null;
                if (urlObj.hostname.includes('youtube.com')) {
                    videoId = urlObj.searchParams.get('v');
                } else if (urlObj.hostname.includes('youtu.be')) {
                    videoId = urlObj.pathname.slice(1).split('?')[0];
                }
                return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
            }
            case 'tiktok': {
                const match = url.match(/\/video\/(\d+)/);
                const videoId = match ? match[1] : null;
                return videoId ? `https://www.tiktok.com/embed/v2/${videoId}` : null;
            }
            case 'instagram': {
                const postMatch = url.match(/\/p\/([^\/\?]+)/);
                const reelMatch = url.match(/\/reel\/([^\/\?]+)/);
                const id = postMatch?.[1] || reelMatch?.[1];
                return id ? `https://www.instagram.com/p/${id}/embed/` : null;
            }
            default:
                return null;
        }
    } catch (error) {
        console.error('Error creating embed URL:', error);
        return null;
    }
};

export const LinkPreview: React.FC<LinkPreviewProps> = ({ url, title: fallbackTitle, variant = "full" }) => {
    const [metadata, setMetadata] = useState<LinkMetadata | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [imageError, setImageError] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);

    // Video detection
    const videoType = getVideoType(url);
    const embedUrl = videoType ? getEmbedUrl(url, videoType) : null;
    const isVideo = Boolean(videoType && embedUrl);

    useEffect(() => {
        const fetchMetadata = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await fetch(
                    `/api/link-preview?url=${encodeURIComponent(url)}`
                );

                if (!response.ok) {
                    throw new Error(ERROR_MESSAGES.GENERAL.FETCH_FAILED);
                }

                const data: LinkMetadata = await response.json();
                setMetadata(data);
            } catch (err) {
                console.error("Error fetching link preview:", err);
                setError(ERROR_MESSAGES.GENERAL.LOAD_FAILED);
            } finally {
                setLoading(false);
            }
        };

        fetchMetadata();
    }, [url]);

    if (loading) {
        if (variant === "compact") {
            return (
                <div className="flex items-center gap-3">
                    <div className="h-12 w-16 rounded-md bg-neutral-200 dark:bg-neutral-800 animate-pulse" />
                    <div className="flex-1 space-y-1">
                        <div className="h-3 w-24 rounded bg-neutral-200 dark:bg-neutral-800 animate-pulse" />
                        <div className="h-3 w-40 rounded bg-neutral-200 dark:bg-neutral-800 animate-pulse" />
                    </div>
                </div>
            );
        }

        return (
            <div className="relative w-full h-full min-h-[400px] flex items-center justify-center bg-neutral-100 dark:bg-neutral-800 rounded-lg">
                <div className="flex flex-col items-center gap-3">
                    <div className="h-8 w-8 rounded-full border-2 border-purple-400 border-t-transparent animate-spin" />
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                        {isVideo ? "Loading video..." : "Loading preview..."}
                    </p>
                </div>
            </div>
        );
    }

    if (error || !metadata) {
        if (variant === "compact") {
            return (
                <div className="flex items-center gap-3 text-xs text-neutral-500 dark:text-neutral-400">
                    <Globe className="h-4 w-4 flex-shrink-0" />
                    <span className="line-clamp-2">{error || "Preview not available"}</span>
                </div>
            );
        }

        return (
            <div className="relative w-full h-full min-h-[400px] flex items-center justify-center bg-neutral-100 dark:bg-neutral-800 rounded-lg">
                <div className="flex flex-col items-center gap-3 text-center px-4">
                    <Globe className="h-12 w-12 text-neutral-400" />
                    <div>
                        <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                            Preview not available
                        </p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                            {error || "Unable to load link metadata"}
                        </p>
                    </div>
                    <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-500 rounded-lg transition-colors"
                    >
                        <ExternalLink className="h-4 w-4" />
                        Open Link
                    </a>
                </div>
            </div>
        );
    }

    const displayTitle = metadata.title || fallbackTitle || "Untitled";
    const displayDomain = new URL(url).hostname.replace(/^www\./, "");

    // Compact variant
    if (variant === "compact") {
        return (
            <div className="flex items-center gap-3">
                {isVideo && !isPlaying && embedUrl ? (
                    <div
                        className="h-14 w-20 flex-shrink-0 overflow-hidden rounded-md bg-neutral-200 dark:bg-neutral-800 relative cursor-pointer group"
                        onClick={() => setIsPlaying(true)}
                    >
                        {metadata.image && !imageError ? (
                            <img
                                src={metadata.image}
                                alt={displayTitle}
                                className="h-full w-full object-cover"
                                onError={() => setImageError(true)}
                            />
                        ) : (
                            <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500">
                                <Play className="h-6 w-6 text-white" />
                            </div>
                        )}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
                            <div className="h-8 w-8 rounded-full bg-white/90 flex items-center justify-center">
                                <Play className="h-4 w-4 text-black ml-0.5" fill="currentColor" />
                            </div>
                        </div>
                    </div>
                ) : metadata.image && !imageError && !isVideo ? (
                    <div className="h-14 w-20 flex-shrink-0 overflow-hidden rounded-md bg-neutral-200 dark:bg-neutral-800">
                        <img
                            src={metadata.image}
                            alt={displayTitle}
                            className="h-full w-full object-cover"
                            onError={() => setImageError(true)}
                        />
                    </div>
                ) : null}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                        {metadata.favicon && (
                            <img
                                src={metadata.favicon}
                                alt=""
                                className="h-3 w-3 rounded-sm flex-shrink-0"
                                onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = "none";
                                }}
                            />
                        )}
                        <span className="text-[11px] text-neutral-500 dark:text-neutral-400 truncate">
                            {metadata.siteName || displayDomain}
                        </span>
                    </div>
                    <div className="text-xs font-medium text-neutral-900 dark:text-neutral-100 line-clamp-2 break-words">
                        {displayTitle}
                    </div>
                </div>
            </div>
        );
    }

    // Full variant
    return (
        <div className="relative w-full h-full min-h-[400px] flex flex-col bg-white dark:bg-neutral-900 rounded-lg overflow-hidden">
            {isVideo && embedUrl ? (
                <>
                    <div className="relative w-full flex-1 bg-neutral-100 dark:bg-neutral-800">
                        <iframe
                            src={embedUrl}
                            className="absolute inset-0 w-full h-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            scrolling="no"
                            frameBorder="0"
                        />
                    </div>

                    <div className="p-4 flex flex-col border-t border-neutral-200 dark:border-neutral-700">
                        <div className="flex items-center gap-2 mb-2">
                            {metadata?.favicon && (
                                <img
                                    src={metadata.favicon}
                                    alt=""
                                    className="h-4 w-4 rounded-sm"
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).style.display = "none";
                                    }}
                                />
                            )}
                            <span className="text-xs text-neutral-500 dark:text-neutral-400 font-medium">
                                {metadata?.siteName || displayDomain}
                            </span>
                        </div>

                        <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-1 line-clamp-1">
                            {displayTitle}
                        </h3>

                        <div className="flex items-center justify-between gap-4 pt-2">
                            <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-neutral-500 dark:text-neutral-400 hover:text-purple-600 dark:hover:text-purple-400 truncate flex-1 transition-colors"
                                title={url}
                            >
                                {url}
                            </a>
                            <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white bg-purple-600 hover:bg-purple-500 rounded-md transition-colors flex-shrink-0"
                            >
                                <ExternalLink className="h-3.5 w-3.5" />
                                Visit
                            </a>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    {/* Thumbnail with optional play button */}
                    {metadata?.image && !imageError && (
                        <div className="relative w-full h-64 bg-neutral-200 dark:bg-neutral-800 flex-shrink-0">
                            <img
                                src={metadata.image}
                                alt={displayTitle}
                                className="w-full h-full object-cover"
                                onError={() => setImageError(true)}
                            />
                        </div>
                    )}

                    <div className="flex-1 p-4 flex flex-col">
                        <div className="flex items-center gap-2 mb-3">
                            {metadata?.favicon && (
                                <img
                                    src={metadata.favicon}
                                    alt=""
                                    className="h-4 w-4 rounded-sm"
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).style.display = "none";
                                    }}
                                />
                            )}
                            <span className="text-xs text-neutral-500 dark:text-neutral-400 font-medium">
                                {metadata?.siteName || displayDomain}
                            </span>
                        </div>

                        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2 line-clamp-2">
                            {displayTitle}
                        </h3>

                        {metadata?.description && (
                            <p className="text-sm text-neutral-600 dark:text-neutral-400 line-clamp-3 mb-4">
                                {metadata.description}
                            </p>
                        )}

                        <div className="flex-1" />

                        <div className="flex items-center justify-between gap-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                            <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-neutral-500 dark:text-neutral-400 hover:text-purple-600 dark:hover:text-purple-400 truncate flex-1 transition-colors"
                                title={url}
                            >
                                {url}
                            </a>
                            <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white bg-purple-600 hover:bg-purple-500 rounded-md transition-colors flex-shrink-0"
                            >
                                <ExternalLink className="h-3.5 w-3.5" />
                                Visit
                            </a>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
